import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const VoteWithId = () => {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [memberCode, setMemberCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [electionInfo, setElectionInfo] = useState(null);
  const [fetchingElection, setFetchingElection] = useState(true);

  // Pre-fill from query param
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam) setMemberCode(idParam);
  }, [searchParams]);

  // Fetch election info for display
  useEffect(() => {
    const fetchElection = async () => {
      try {
        const response = await axios.get(`${API_URL}/elections/${electionId}/public`);
        setElectionInfo(response.data.data);
      } catch (err) {
        console.error('Failed to fetch election:', err);
      } finally {
        setFetchingElection(false);
      }
    };
    fetchElection();
  }, [electionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberCode.trim()) {
      setError('Please enter your Voter ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/voting-auth/authenticate-by-id`, {
        memberCode: memberCode.trim(),
        electionId,
      });

      if (response.data.success) {
        const { sessionId, electionId: eid, voterName } = response.data;
        // Store voter name for the kiosk flow
        sessionStorage.setItem('kioskVoterName', voterName);
        sessionStorage.setItem('kioskElectionId', electionId);
        sessionStorage.setItem('kioskVotingMode', 'id_based');
        navigate(`/elections/${eid}/vote?sessionId=${sessionId}`);
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(
        err.response?.data?.error ||
        'Failed to authenticate. Please check your ID and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetchingElection) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div style={{ width: '100%', maxWidth: '440px', padding: '0 1rem' }}>
        {/* Logo */}
        <div className="text-center mb-4">
          <Logo color="white" width="250px" />
        </div>

        {/* Card */}
        <div className="card shadow-lg" style={{ borderRadius: '16px', border: 'none' }}>
          <div className="card-body p-4 p-md-5">
            {/* Election Info */}
            {electionInfo && (
              <div className="text-center mb-4">
                {electionInfo.organization?.logoUrl && (
                  <img src={electionInfo.organization.logoUrl} alt="" style={{ height: '48px', marginBottom: '0.75rem', borderRadius: '8px' }} />
                )}
                <h4 style={{ fontWeight: '700', color: '#2c3e50' }}>{electionInfo.name}</h4>
                {electionInfo.organization?.name && (
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    {electionInfo.organization.name}
                  </p>
                )}
              </div>
            )}

            <hr className="my-3" />

            <h6 className="text-center mb-4" style={{ color: '#6b7280' }}>
              Enter your Voter ID to vote
            </h6>

            {error && (
              <div className="alert alert-danger py-2" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <input
                  type="text"
                  className="form-control form-control-lg text-center"
                  placeholder="e.g. STU-0001"
                  value={memberCode}
                  onChange={(e) => {
                    setMemberCode(e.target.value.toUpperCase());
                    setError(null);
                  }}
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    letterSpacing: '2px',
                    borderRadius: '12px',
                    padding: '0.75rem 1rem',
                  }}
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="btn w-100"
                disabled={loading || !memberCode.trim()}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: '600',
                  padding: '0.75rem',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  border: 'none',
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Proceed to Vote
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <p className="text-center text-white mt-3" style={{ opacity: 0.7, fontSize: '0.8rem' }}>
          Powered by VoteWave
        </p>
      </div>
    </div>
  );
};

export default VoteWithId;
